/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useServiceContext } from '../../../root'
import type { DocumentType } from '../../../services/api/api.documenttypes'
import TableMenu from './TableMenu'
import { RcmTable } from '../../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../components/table/TableComponents'
import { createDateTimeFormatter } from '../../../utils/commonColumns'

interface DoctypesTableProps {
  onEditDocumentType: (documentType: DocumentType) => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

const DocumentTypesTable: React.FC<DoctypesTableProps> = ({
  onEditDocumentType,
  onDeleteSuccess,
  onExportStart,
  onExportEnd
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const formatDateTime = createDateTimeFormatter(context.service.auth, translate)

  const handleRowClick = (row: DocumentType) => {
    onEditDocumentType(row)
  }

  return (
    <RcmTable
      className="chita-table"
      noResultsText={translate('NO_RESULTS_FILTERED')}
      HeadRow={
        <TableHeadRow className="has-actions">
          <TableHeadCell style={{ fontSize: '12px' }} sort="NAME">
            {translate('NAME')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} id="78a6fa5a-4ab2-4c1c-a405-f5ece187d1ab">
            {translate('DESCRIPTION')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="CREATED_AT">
            {translate('CREATED_ON')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="CREATED_BY">
            {translate('CREATED_BY')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="UPDATED_AT">
            {translate('TABLE_TH_MODIFIED_ON')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} sort="UPDATED_BY">
            {translate('TABLE_TH_MODIFIED_BY')}
          </TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }} id="1b1e4c70-e09a-4299-9e7f-fbff6e450d68" className="actions">
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={({ row }: { row: DocumentType }) => {
        return (
          <TableRow key={row.id}>
            <td className="clipped-cell">
              <div className="ellipsis">
                {row.system ? (
                  row.name
                ) : (
                  <span className="btn-link" onClick={() => handleRowClick(row)} style={{ cursor: 'pointer' }}>
                    {row.name}
                  </span>
                )}
              </div>
            </td>
            <td>{row.description || translate('NOT_AVAILABLE')}</td>
            <td>{formatDateTime(row.createdAt)}</td>
            <td>{row.createdBy && row.createdBy.name ? row.createdBy.name : translate('NOT_AVAILABLE')}</td>
            <td>{formatDateTime(row.updatedAt)}</td>
            <td>{row.updatedBy && row.updatedBy.name ? row.updatedBy.name : translate('NOT_AVAILABLE')}</td>
            <td className="actions">
              <TableMenu
                documentType={row}
                onEditDocumentType={onEditDocumentType}
                onDeleteSuccess={onDeleteSuccess}
                onExportStart={onExportStart}
                onExportEnd={onExportEnd}
              />
            </td>
          </TableRow>
        )
      }}
    />
  )
}

export default DocumentTypesTable
